import { useTranslation } from "@/hooks";
import { useCreateRelation, useGetIdFromName } from "@/services";
import { currentUserAtom } from "@/store/login";
import { throwOnDev, useDebounce } from "@/utils";
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Icon,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";

const MIN_USERNAME_LEN = 4;

type RelationRole = "seller" | "buyer";
type UsernameStatus =
	| "idle"
	| "tooShort"
	| "checking"
	| "found"
	| "notFound"
	| "error";

const isNonEmptyString = (v: unknown): v is string =>
	typeof v === "string" && v.trim().length > 0;

interface RelationsAddRequestProps {
	relationRole: RelationRole;
	buttonLabel?: string;
	dialogTitle?: string;
}

const RelationsAddRequest: React.FC<RelationsAddRequestProps> = ({
	relationRole: fixedRole,
	buttonLabel,
	dialogTitle,
}) => {
	const { t } = useTranslation();

	const user = useAtomValue(currentUserAtom);
	const createRelationMutation = useCreateRelation();

	const [isOpen, setIsOpen] = useState(false);
	const [username, setUsername] = useState("");

	const debouncedUsername = useDebounce(username, 1_000);

	const {
		data: userData,
		error,
		isFetching,
		refetch,
	} = useGetIdFromName(
		{ query: { name: debouncedUsername.trim() } },
		{ enabled: false },
	);

	useEffect(() => {
		const q = debouncedUsername.trim();
		if (q.length >= MIN_USERNAME_LEN) {
			refetch();
		}
	}, [debouncedUsername, refetch]);

	const trimmed = debouncedUsername.trim();
	const hasTyped = trimmed.length > 0;
	const tooShort = hasTyped && trimmed.length < MIN_USERNAME_LEN;

	const requestUserId: string | null = useMemo(
		() => (isNonEmptyString(userData) ? userData.trim() : null),
		[userData],
	);

	const status: UsernameStatus = useMemo(() => {
		if (!hasTyped) return "idle";
		if (tooShort) return "tooShort";
		if (isFetching) return "checking";
		if (error) return "error";
		return requestUserId ? "found" : "notFound";
	}, [hasTyped, tooShort, isFetching, error, requestUserId]);

	const canSend = status === "found";
	const showNotFound = status === "notFound";

	const helperText = useMemo(() => {
		switch (status) {
			case "idle":
				return " ";
			case "tooShort":
				return t("validation.atLeast", {
					field: t("relation.addRequest.username"),
					num: MIN_USERNAME_LEN,
				});
			case "checking":
				return t("relation.addRequest.usernameChecking");
			case "error":
				return t("relation.addRequest.usernameCheckError");
			case "found":
				return t("relation.addRequest.usernameAvailable");
			case "notFound":
				return t("relation.addRequest.usernameNotFound");
			default:
				status satisfies never;
				throwOnDev(`Unknown username status: ${status}.`);
		}
	}, [status, t]);

	const resetData = () => {
		setIsOpen(false);
		setUsername("");
	};

	const handleSendRequest = async () => {
		if (!user?.id || !requestUserId) {
			throwOnDev("Missing user id(s) to create relation.");
		}
		createRelationMutation.mutate(
			{
				body:
					fixedRole === "buyer"
						? {
								buyer_id: requestUserId,
								seller_id: user.id,
								requester: "SELLER" as const,
								is_allowed_to_order: true,
							}
						: {
								buyer_id: user.id,
								seller_id: requestUserId,
								requester: "BUYER" as const,
							},
			},
			{
				onSuccess: () => {
					resetData();
				},
			},
		);
	};

	return (
		<>
			<Button
				type="button"
				color="primary"
				className="normal-case"
				onClick={() => setIsOpen(true)}
			>
				{buttonLabel ?? t("relation.addRequestButton")}
			</Button>

			<Dialog open={isOpen} onOpenChange={(o) => !o && resetData()}>
				<DialogContent className="w-full max-w-120">
					<DialogHeader>
						<DialogTitle>
							{dialogTitle ?? t("relation.addRequest.title")}
						</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea className="flex flex-col gap-4 max-h-[90vh] pt-0">
						{!fixedRole && (
							<RadioGroup
								value={fixedRole}
								orientation="horizontal"
								className="flex flex-row gap-6"
							>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="seller" id="relation-role-seller" />
									<Label htmlFor="relation-role-seller">
										{t("relation.addRequest.seller")}
									</Label>
								</div>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="buyer" id="relation-role-buyer" />
									<Label htmlFor="relation-role-buyer">
										{t("relation.addRequest.buyer")}
									</Label>
								</div>
							</RadioGroup>
						)}

						<div className="flex flex-col gap-4 sm:flex-row">
							<div className="w-full flex-1">
								<Label htmlFor="relation-add-username" className="mb-1">
									{t("relation.addRequest.username")}
								</Label>
								<Input
									id="relation-add-username"
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									autoFocus
									validation={showNotFound || !!error ? "invalid" : "none"}
									append={
										status === "checking" ? (
											<Icon name="loader-line" size="sm" />
										) : status === "found" ? (
											<Icon
												name="check-line"
												size="sm"
												className="text-success-text"
											/>
										) : status === "notFound" ? (
											<Icon
												name="close-line"
												size="sm"
												className="text-danger-text"
											/>
										) : undefined
									}
								/>
								{status === "found" ? (
									<p className="mt-1 text-xs text-success-text">{helperText}</p>
								) : (
									<p
										className={`mt-1 text-xs ${
											showNotFound || error
												? "text-danger-text"
												: "text-secondary-icon"
										}`}
									>
										{helperText.trim() || "\u00A0"}
									</p>
								)}
							</div>

							<div className="flex-none mt-0 sm:mt-6">
								<Button
									type="button"
									color="primary"
									className="w-full sm:w-auto"
									disabled={!canSend}
									loading={createRelationMutation.isPending}
									onClick={handleSendRequest}
								>
									{t("relation.addRequest.buttonSend")}
								</Button>
							</div>
						</div>
					</DialogScrollableArea>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default RelationsAddRequest;
